import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import './ReassignModal.css';

// Reassign candidates (October 2026). Opened from a recruiter's row in Manage Users, admins + GV only
// (see shared/canReassign.js; the server enforces it in routes/reassign.js). Pick customers, which
// candidates, and who receives them; the preview is computed by the server with the same rules it
// uses to move them. Past moves for this recruiter are listed underneath with Undo.
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtWhen = iso => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

export default function ReassignModal({ fromUser, onClose }) {
  const [scope, setScope] = useState('open');
  const [mode, setMode] = useState('owner');
  const [toId, setToId] = useState('');
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [history, setHistory] = useState([]);
  const [undoMsg, setUndoMsg] = useState('');

  const loadSummary = useCallback(async () => {
    setError('');
    try {
      const r = await axios.get(`${API_URL}/api/reassign/${fromUser.id}/summary?scope=${scope}`, auth());
      setSummary(r.data);
      setSelected(new Set(r.data.companies.map(c => c.company_id)));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load this recruiter\'s candidates.');
    }
  }, [fromUser.id, scope]);

  const loadHistory = useCallback(async () => {
    try { setHistory((await axios.get(`${API_URL}/api/reassign/${fromUser.id}/history`, auth())).data); } catch { setHistory([]); }
  }, [fromUser.id]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Preview follows every change of customers / scope / target.
  useEffect(() => {
    if (!summary) return;
    if (mode === 'one' && !toId) { setPreview(null); return; }
    let alive = true;
    const body = { scope, mode, toRecruiterId: toId ? Number(toId) : null, companyIds: [...selected] };
    axios.post(`${API_URL}/api/reassign/${fromUser.id}/preview`, body, auth())
      .then(r => { if (alive) setPreview(r.data); })
      .catch(e => { if (alive) setPreview({ error: e.response?.data?.error || 'Could not build the preview.' }); });
    return () => { alive = false; };
  }, [summary, selected, scope, mode, toId, fromUser.id]);

  const toggle = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = summary && selected.size === summary.companies.length && summary.companies.length > 0;
  const canConfirm = preview && !preview.error && preview.total > 0 && preview.unresolved.length === 0 && !busy;

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API_URL}/api/reassign/${fromUser.id}`,
        { scope, mode, toRecruiterId: toId ? Number(toId) : null, companyIds: [...selected] }, auth());
      setDone(r.data);
      await Promise.all([loadSummary(), loadHistory()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not reassign.');
    } finally {
      setBusy(false);
    }
  };

  const undo = async batch => {
    setUndoMsg('');
    try {
      const r = (await axios.post(`${API_URL}/api/reassign/batch/${batch.id}/undo`, {}, auth())).data;
      setUndoMsg(`Moved ${r.restored} back to ${firstName(fromUser.name)}${r.skipped ? `. ${r.skipped} were moved again since and were left alone.` : '.'}`);
      await Promise.all([loadSummary(), loadHistory()]);
    } catch (e) {
      setUndoMsg(e.response?.data?.error || 'Could not undo.');
    }
  };

  const total = summary ? summary.companies.reduce((s, c) => s + c.count, 0) : 0;

  return (
    <div className="ra-overlay" onClick={onClose}>
      <div className="ra-modal" role="dialog" aria-labelledby="ra-title" onClick={e => e.stopPropagation()}>
        <div className="ra-head">
          <div>
            <h2 id="ra-title">Reassign candidates</h2>
            <p className="ra-sub">Choose which of {firstName(fromUser.name)}'s candidates move, and to whom. Nothing changes until you confirm.</p>
          </div>
          <button className="ra-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ra-body">
          <div className="ra-field">
            <div className="ra-label">From</div>
            <div className="ra-from">
              <div className="ra-avatar">{initials(fromUser.name || fromUser.email)}</div>
              <div>
                <b>{fromUser.name || fromUser.email}</b> {fromUser.locked_at && <span className="ra-badge-locked">Locked</span>}
                {summary && <div className="ra-sub">{summary.open_count} in progress · {summary.all_count} in total</div>}
              </div>
            </div>
          </div>

          <div className="ra-field">
            <div className="ra-label">Which candidates</div>
            <div className="ra-seg">
              <label className={`ra-opt${scope === 'open' ? ' on' : ''}`}>
                <input type="radio" name="ra-scope" checked={scope === 'open'} onChange={() => setScope('open')} />
                <div><b>In progress only</b><span>Not archived, not sent to client, not hired. Recommended.</span></div>
              </label>
              <label className={`ra-opt${scope === 'all' ? ' on' : ''}`}>
                <input type="radio" name="ra-scope" checked={scope === 'all'} onChange={() => setScope('all')} />
                <div><b>Everything</b><span>Also archived, sent-to-client and hired candidates.</span></div>
              </label>
            </div>
          </div>

          <div className="ra-field">
            <div className="ra-label">Customers <small>· tick one or more</small></div>
            {!summary && !error && <div className="ra-sub">Loading…</div>}
            {summary && summary.companies.length === 0 && <div className="ra-sub">No candidates to move.</div>}
            {summary && summary.companies.length > 0 && (
              <div className="ra-custlist">
                <label className="ra-cust ra-cust-all">
                  <input type="checkbox" checked={allOn}
                    onChange={e => setSelected(e.target.checked ? new Set(summary.companies.map(c => c.company_id)) : new Set())} />
                  <span>All customers</span><span className="ra-count">{total}</span><span />
                </label>
                {summary.companies.map(c => (
                  <label key={c.company_id} className="ra-cust">
                    <input type="checkbox" checked={selected.has(c.company_id)} onChange={() => toggle(c.company_id)} />
                    <span>{c.company_name}</span>
                    <span className="ra-count">{c.count}</span>
                    <span className={`ra-owner${c.owner_problem ? ' warn' : ''}`}>
                      {c.owner_problem || `Account recruiter: ${c.owner_name}`}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="ra-field">
            <div className="ra-label">Move to</div>
            <div className="ra-seg">
              <label className={`ra-opt${mode === 'owner' ? ' on' : ''}`}>
                <input type="radio" name="ra-mode" checked={mode === 'owner'} onChange={() => setMode('owner')} />
                <div><b>Each customer's recruiter</b><span>Uses the Recruiter set on the Accounts page.</span></div>
              </label>
              <label className={`ra-opt${mode === 'one' ? ' on' : ''}`}>
                <input type="radio" name="ra-mode" checked={mode === 'one'} onChange={() => setMode('one')} />
                <div><b>One recruiter</b>
                  <span>
                    <select value={toId} onChange={e => { setToId(e.target.value); setMode('one'); }} aria-label="Recruiter to receive the candidates">
                      <option value="">Choose…</option>
                      {(summary?.targets || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="ra-field">
            <div className="ra-label">Preview</div>
            <div className="ra-preview">
              {!preview && <div className="ra-sub">{mode === 'one' && !toId ? 'Choose a recruiter to see the preview.' : 'Working it out…'}</div>}
              {preview?.error && <div className="ra-warn">{preview.error}</div>}
              {preview && !preview.error && (
                <>
                  <div className="ra-total">{preview.total} candidate{preview.total === 1 ? '' : 's'} will move</div>
                  {preview.groups.map(g => (
                    <div className="ra-pline" key={g.to_id}>
                      <span>→ <b>{g.to_name}</b> <span className="ra-sub">({g.companies.map(c => c.company_name).join(', ')})</span></span>
                      <span className="ra-n">{g.count}</span>
                    </div>
                  ))}
                  {preview.unresolved.map(u => (
                    <div className="ra-warn" key={u.company_name}>
                      {u.count} from {u.company_name}: {u.reason}. Untick it, pick "One recruiter", or set its recruiter on Accounts first.
                    </div>
                  ))}
                  <div className="ra-pnote">Open tasks move too. Next automated texts go from the new recruiter's number. Candidates are not texted about the change. Reports keep {firstName(fromUser.name)} as the uploader.</div>
                </>
              )}
            </div>
          </div>

          {error && <div className="ra-warn">{error}</div>}
          {done && (
            <div className="ra-success">
              Moved {done.moved} candidate{done.moved === 1 ? '' : 's'}{done.tasks_moved ? ` and ${done.tasks_moved} open task${done.tasks_moved === 1 ? '' : 's'}` : ''}. You can undo it below.
            </div>
          )}

          {history.length > 0 && (
            <div className="ra-field">
              <div className="ra-label">Past reassignments from {firstName(fromUser.name)}</div>
              <div className="ra-history">
                {history.map(b => (
                  <div className="ra-hrow" key={b.id}>
                    <span>
                      <span className="ra-when">{fmtWhen(b.created_at)}</span> · {b.candidate_count ?? 0} candidate{b.candidate_count === 1 ? '' : 's'}
                      {' → '}{b.targets.map(t => `${firstName(t.to_name)} (${t.n})`).join(', ') || 'nobody'}
                      {b.mode === 'single' ? ' · from the edit form' : ''} · by {firstName(b.actor_name)}
                    </span>
                    {b.undone_at
                      ? <span className="ra-sub">Undone {fmtWhen(b.undone_at)}{b.undone_by_name ? ` by ${firstName(b.undone_by_name)}` : ''}</span>
                      : <button className="ra-undo" onClick={() => undo(b)}>Undo</button>}
                  </div>
                ))}
              </div>
              {undoMsg && <div className="ra-success">{undoMsg}</div>}
            </div>
          )}
        </div>

        <div className="ra-foot">
          <button className="ra-btn" onClick={onClose}>{done ? 'Close' : 'Cancel'}</button>
          <button className="ra-btn ra-btn-primary" disabled={!canConfirm} onClick={confirm}>
            {busy ? 'Moving…' : preview?.total ? `Reassign ${preview.total}` : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function firstName(n) { return (n || '').trim().split(/\s+/)[0] || ''; }
function initials(n) { return (n || '?').split(/[\s@]+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join(''); }
